import { APPROVAL_STATUS_FILTER } from '../../../../constants/hardware-inventory';
import { approvedVersionOf, isSameComponentType } from '../../../../helpers/hardwareInventory';

/*
 * Pure derivations for the fleet hardware inventory dashboard.
 *
 * Kept dependency-free on purpose: `moment` and `_` are webpack ProvidePlugin globals and are not
 * defined when mocha loads this module, so anything reachable from a test must not rely on them.
 */

export const hasNoApprovedVersion = (componentType) => {
	return !approvedVersionOf(componentType);
};

/*
 * Narrows the catalog by whether anyone has recorded an approved version for the type.
 *
 * Note what this does not claim. It answers "has a decision been recorded here yet", not "is the
 * fleet running the right firmware" - the second needs the API's version-aware ranking, which this
 * endpoint does not carry. An unrecognised status returns the catalog untouched, so a stale value
 * degrades to showing everything rather than to showing nothing.
 */
export const filterByApprovalStatus = (componentTypes, status) => {
	const catalog = componentTypes ?? [];

	if (status === APPROVAL_STATUS_FILTER.APPROVED) {
		return catalog.filter((componentType) => {
			return !hasNoApprovedVersion(componentType);
		});
	}

	if (status === APPROVAL_STATUS_FILTER.NOT_APPROVED) {
		return catalog.filter(hasNoApprovedVersion);
	}

	return catalog;
};

/*
 * A miss is expected rather than an error: the analytics table is an inner join, so a component
 * type has no row between its creation and the next refresh covering it.
 */
export const findComponentTypeFor = (component, componentTypes) => {
	if (!component) {
		return null;
	}

	return (componentTypes ?? []).find((componentType) => {
		return isSameComponentType(component, componentType);
	}) ?? null;
};

/*
 * The `manufacturer` and `model` filters match the *normalized* catalog values, which are lowercased
 * and hyphenated (`xeon-silver-4310`), while the table shows the human `displayName`
 * ("Intel Xeon Silver 4310"). Typing what you can read therefore has to be normalized first, or a
 * partial match against the normalized column never fires.
 */
export const normalizeFilterValue = (value) => {
	return String(value ?? '')
		.trim()
		.toLowerCase()
		.replace(/\s+/g, '-')
		.replace(/-+/g, '-');
};

const uniqueValues = (componentTypes, field) => {
	return [...new Set(componentTypes.map((componentType) => {
		return componentType?.[field];
	}).filter(Boolean))];
};

/*
 * Resolves one free-text query into the (manufacturer, model) pair the API can actually filter on.
 *
 * The endpoint has no free-text parameter and ANDs its filters, so a single box cannot search
 * across fields server-side. What makes this work anyway is that the component type catalog is
 * already loaded for the firmware tab, and it carries `displayName` next to the normalized
 * `manufacturer` and `model`. The query is resolved against that catalog, so "Syneto Hyperion"
 * becomes manufacturer=syneto + model=hyperion exactly, rather than being guessed at.
 *
 * Precedence, first hit wins:
 *   1. `displayName` contains the query. Because displayName reads "<Manufacturer> <Model>", this
 *      covers both a bare vendor ("seagate" -> every Seagate type, so manufacturer only) and a
 *      full name ("Intel Xeon Silver 4310" -> one type, so both fields).
 *   2. A known normalized model contains the hyphenated query.
 *   3. A known manufacturer contains the query.
 *   4. Nothing in the catalog matched - fall back to a partial model match. The catalog is an inner
 *      join on the analytics table, so a component whose type has no analytics row yet is only
 *      reachable this way.
 */
export const analyseSmartSearch = (query, componentTypes) => {
	const plain = String(query ?? '').trim().toLowerCase();

	if (!plain) {
		return { status: 'empty', filters: { manufacturer: '', model: '' } };
	}

	const hyphenated = normalizeFilterValue(plain);
	const catalog = componentTypes ?? [];

	const byDisplayName = catalog.filter((componentType) => {
		return String(componentType?.displayName ?? '').toLowerCase().includes(plain);
	});

	if (byDisplayName.length > 0) {
		const manufacturers = uniqueValues(byDisplayName, 'manufacturer');
		const models = uniqueValues(byDisplayName, 'model');

		if (manufacturers.length === 1 && models.length === 1) {
			return {
				status: 'type',
				matchCount: byDisplayName.length,
				displayName: byDisplayName[0].displayName,
				filters: { manufacturer: manufacturers[0], model: models[0] }
			};
		}

		if (manufacturers.length === 1) {
			return {
				status: 'manufacturer',
				matchCount: byDisplayName.length,
				manufacturer: manufacturers[0],
				filters: { manufacturer: manufacturers[0], model: '' }
			};
		}

		/*
		 * The query spans several manufacturers, and the API ANDs its filters, so there is no way to
		 * ask for "intel OR seagate". No filter is applied - the caller has to say so, because
		 * silently listing the whole fleet after someone typed a query reads as a broken control.
		 */
		return {
			status: 'ambiguous',
			matchCount: byDisplayName.length,
			manufacturerCount: manufacturers.length,
			filters: { manufacturer: '', model: '' }
		};
	}

	const matchesKnownModel = catalog.some((componentType) => {
		return String(componentType?.model ?? '').includes(hyphenated);
	});

	if (matchesKnownModel) {
		return { status: 'model', value: hyphenated, filters: { manufacturer: '', model: hyphenated } };
	}

	const matchesKnownManufacturer = catalog.some((componentType) => {
		return String(componentType?.manufacturer ?? '').includes(plain);
	});

	if (matchesKnownManufacturer) {
		return { status: 'manufacturer', manufacturer: plain, filters: { manufacturer: plain, model: '' } };
	}

	return { status: 'unknown', value: hyphenated, filters: { manufacturer: '', model: hyphenated } };
};

/*
 * The API rebuilds analytics rows per type, so there is no single fleet-wide refresh timestamp.
 * The most recent one across the loaded rows is the closest honest answer to "how fresh is this?".
 */
export const latestRefreshedAt = (componentTypes) => {
	return (componentTypes ?? [])
		.map((componentType) => {
			return componentType?.refreshedAt;
		})
		.filter(Boolean)
		.sort()
		.pop() ?? null;
};
